import { Injectable, Logger } from '@nestjs/common';
import { Cron } from '@nestjs/schedule';
import { PrismaService, S3CompatibleAdapter } from '@cart/api-data-access';
import { randomUUID } from 'node:crypto';
import { EmailService } from '../notifications/email.service';
import { backupEnvPrefix, readBackupStorageEnv } from '../database/backup-storage.constants';

/**
 * How often the probe runs. Daily at 08:00.
 *
 * Read at module load — `load-env.ts` is the first import in main.ts, so `.env`
 * is populated before this evaluates, and PM2 supplies the environment directly
 * in production. Set CONNECTIVITY_CHECK_CRON to '*\/15 * * * *' to go back to
 * every 15 minutes while debugging a connectivity problem.
 */
export const CONNECTIVITY_CHECK_CRON = process.env.CONNECTIVITY_CHECK_CRON ?? '0 8 * * *';

/**
 * The timezone that expression is read in.
 *
 * Without this, "daily at 08:00" means 08:00 in whatever timezone the server
 * process happens to run in — 13:30 IST on a UTC box, which is not what anyone
 * picking "8am" intended. The backup policies already pin Asia/Kolkata for the
 * same reason; a schedule that silently shifts with the host is a schedule
 * nobody can reason about.
 */
export const CONNECTIVITY_CHECK_TIMEZONE = process.env.CONNECTIVITY_CHECK_TZ ?? 'Asia/Kolkata';

/** Where the report goes. */
export const CONNECTIVITY_ALERT_EMAIL =
  process.env.HEALTHCHECK_ALERT_EMAIL ?? 'sudharshanreddyjanike@gmail.com';

/**
 * `always` emails every run; `changes` emails only failures and recoveries.
 *
 * Defaults to `always`: on a daily schedule that is one mail a day, which also
 * doubles as proof the check itself is still running — a silent monitor and a
 * healthy system look identical otherwise.
 *
 * It matters more if the interval is ever shortened again. At *\/15 `always` is
 * 96 mails a day, and an inbox receiving 96 identical "all good" messages is one
 * where the message saying FAILED goes unread. `changes` still mails every
 * failure and every recovery — it drops only the repeated good news.
 */
export const CONNECTIVITY_EMAIL_MODE = (process.env.HEALTHCHECK_EMAIL_MODE ?? 'always') as
  | 'always'
  | 'changes';

/** Probe objects older than this are litter from a run that died mid-flight. */
const PROBE_TTL_MS = 60 * 60 * 1000;

export interface CheckResult {
  name: string;
  ok: boolean;
  durationMs: number;
  detail: string;
}

export interface ConnectivityReport {
  ok: boolean;
  ranAt: Date;
  environment: string;
  checks: CheckResult[];
  totalMs: number;
}

/**
 * Prove — on a schedule — that the things this platform cannot work without are
 * actually reachable.
 *
 * ── WHY THIS EXISTS ─────────────────────────────────────────────────────────
 *
 * Every dependency here has already failed silently at least once: R2
 * credentials that were configured but never read, because nothing loaded
 * `.env` into the bundled process; a backup subsystem that reported a backend
 * it had never looked for; policies that sat "enabled" and unrunnable for days.
 * The common shape is that nothing exercised the dependency until the moment it
 * was needed, which is the worst moment to find out.
 *
 * So this does the real thing rather than inspecting configuration: it queries
 * the database, and it writes an object to R2, reads the bytes back, compares
 * them, and deletes it. Configuration that *looks* right is what produced every
 * one of the failures above.
 */
@Injectable()
export class ConnectivityCheckService {
  private readonly logger = new Logger(ConnectivityCheckService.name);

  /** Previous outcome, so `changes` mode can tell a recovery from a repeat. */
  private lastOk: boolean | null = null;

  constructor(
    private readonly prisma: PrismaService,
    private readonly email: EmailService
  ) {}

  @Cron(CONNECTIVITY_CHECK_CRON, {
    name: 'connectivity-check',
    timeZone: CONNECTIVITY_CHECK_TIMEZONE,
  })
  async scheduled(): Promise<void> {
    const report = await this.run();
    await this.notify(report);
  }

  /**
   * Run every check. Never throws: a health check that crashes the scheduler
   * has become the outage it was meant to report.
   */
  async run(): Promise<ConnectivityReport> {
    const started = Date.now();
    const checks: CheckResult[] = [];

    checks.push(await this.checkDatabase());
    checks.push(...(await this.checkObjectStorage()));

    const report: ConnectivityReport = {
      ok: checks.every((c) => c.ok),
      ranAt: new Date(),
      environment: backupEnvPrefix(),
      checks,
      totalMs: Date.now() - started,
    };

    this.log(report);
    return report;
  }

  // ── The checks ─────────────────────────────────────────────────────────────

  private async checkDatabase(): Promise<CheckResult> {
    const started = Date.now();

    try {
      // A real round trip through the pool. Reading pg_backend_pid() also
      // proves a backend answered, rather than a constant the planner folded.
      const rows = await this.prisma.$queryRaw<Array<{ pid: number; db: string }>>`
        SELECT pg_backend_pid() AS pid, current_database() AS db
      `;

      const row = rows[0];
      return {
        name: 'database',
        ok: Boolean(row?.db),
        durationMs: Date.now() - started,
        detail: row?.db ? `connected to "${row.db}" (backend pid ${row.pid})` : 'query returned no row',
      };
    } catch (err) {
      return {
        name: 'database',
        ok: false,
        durationMs: Date.now() - started,
        detail: (err as Error).message,
      };
    }
  }

  /**
   * List, write, read back, verify the bytes, delete.
   *
   * Four separate checks because they fail for different reasons and are fixed
   * differently: a failing list is usually credentials or the endpoint; a
   * failing write on a working list is almost always a token scoped read-only;
   * a mismatching read-back is the interesting one, and the reason this
   * compares bytes instead of trusting a 200.
   */
  private async checkObjectStorage(): Promise<CheckResult[]> {
    const env = readBackupStorageEnv();

    if (!env) {
      const detail =
        'No R2 credentials: set PLATFORM_R2_BUCKET/ENDPOINT/ACCESS_KEY_ID/SECRET_ACCESS_KEY ' +
        '(or the BACKUP_* equivalents).';
      return ['r2_list', 'r2_write', 'r2_read', 'r2_delete'].map((name) => ({
        name,
        ok: false,
        durationMs: 0,
        detail,
      }));
    }

    const storage = new S3CompatibleAdapter({
      bucket: env.bucket,
      region: env.region,
      endpoint: env.endpoint,
      accessKeyId: env.accessKeyId,
      secretAccessKey: env.secretAccessKey,
      forcePathStyle: true,
    });

    const prefix = `${backupEnvPrefix()}/health/connectivity`;
    // Epoch millis lead the name so the sweep can tell a probe's age from the
    // listing alone. `list()` returns keys and nothing else, so the alternative
    // is a getMetadata() call per object to learn something the name can carry
    // for free.
    const key = `${prefix}/${Date.now()}-${randomUUID()}.txt`;
    const body = Buffer.from(`forgestack connectivity probe ${new Date().toISOString()}`, 'utf8');

    const results: CheckResult[] = [];
    let wrote = false;

    // 1. List.
    let started = Date.now();
    try {
      const page = await storage.list(`${prefix}/`, undefined);
      results.push({
        name: 'r2_list',
        ok: true,
        durationMs: Date.now() - started,
        detail: `bucket "${env.bucket}", prefix "${prefix}/" — ${page.keys.length} object(s)`,
      });
    } catch (err) {
      results.push({
        name: 'r2_list',
        ok: false,
        durationMs: Date.now() - started,
        detail: (err as Error).message,
      });
    }

    // 2. Write.
    started = Date.now();
    try {
      await storage.save(body, key, 'text/plain');
      wrote = true;
      results.push({
        name: 'r2_write',
        ok: true,
        durationMs: Date.now() - started,
        detail: `wrote ${body.length} bytes to ${key}`,
      });
    } catch (err) {
      results.push({
        name: 'r2_write',
        ok: false,
        durationMs: Date.now() - started,
        detail: (err as Error).message,
      });
    }

    // 3. Read back — and compare, because a 200 carrying the wrong bytes is
    //    exactly the failure a status-code check cannot see.
    started = Date.now();
    if (wrote) {
      try {
        const readBack = await storage.read(key);
        const identical = readBack.equals(body);
        results.push({
          name: 'r2_read',
          ok: identical,
          durationMs: Date.now() - started,
          detail: identical
            ? `read ${readBack.length} bytes back, identical`
            : `read ${readBack.length} bytes, expected ${body.length} — content differs`,
        });
      } catch (err) {
        results.push({
          name: 'r2_read',
          ok: false,
          durationMs: Date.now() - started,
          detail: (err as Error).message,
        });
      }
    } else {
      results.push({ name: 'r2_read', ok: false, durationMs: 0, detail: 'skipped — nothing was written to read' });
    }

    // 4. Delete. Also the cleanup, so a passing probe leaves nothing behind.
    started = Date.now();
    if (wrote) {
      try {
        await storage.delete(key);
        results.push({
          name: 'r2_delete',
          ok: true,
          durationMs: Date.now() - started,
          detail: `deleted ${key}`,
        });
      } catch (err) {
        results.push({
          name: 'r2_delete',
          ok: false,
          durationMs: Date.now() - started,
          detail: `${(err as Error).message} — probe object left at ${key}`,
        });
      }
    } else {
      results.push({ name: 'r2_delete', ok: true, durationMs: 0, detail: 'nothing to delete' });
    }

    await this.sweepStaleProbes(storage, prefix);
    return results;
  }

  /**
   * Remove probe objects an earlier run failed to clean up.
   *
   * Small, but the bucket is on a 1 GB budget and this runs 96 times a day; a
   * leak measured in objects per day is still a leak.
   */
  private async sweepStaleProbes(storage: S3CompatibleAdapter, prefix: string): Promise<void> {
    try {
      const page = await storage.list(`${prefix}/`, undefined);
      const cutoff = Date.now() - PROBE_TTL_MS;

      const stale = page.keys.filter((objectKey) => {
        // `<prefix>/<epochMillis>-<uuid>.txt`. Anything that does not parse is
        // left alone: this sweep deletes, so it errs towards doing nothing.
        const stamp = Number(objectKey.slice(prefix.length + 1).split('-')[0]);
        return Number.isFinite(stamp) && stamp > 0 && stamp < cutoff;
      });

      if (stale.length) {
        await storage.deleteMany(stale);
        this.logger.log(`swept ${stale.length} stale probe object(s) from ${prefix}/`);
      }
    } catch (err) {
      // Never fail the report over housekeeping.
      this.logger.warn(`probe sweep skipped: ${(err as Error).message}`);
    }
  }

  // ── Output ─────────────────────────────────────────────────────────────────

  private log(report: ConnectivityReport): void {
    // One line per check, so grepping the PM2 log for a single check's history
    // works without parsing a blob.
    for (const check of report.checks) {
      const line =
        `connectivity ${check.name}: ${check.ok ? 'ok' : 'FAILED'} ` +
        `(${check.durationMs}ms) — ${check.detail}`;
      if (check.ok) this.logger.log(line);
      else this.logger.error(line);
    }

    const summary =
      `connectivity ${report.ok ? 'OK' : 'FAILED'} ` +
      `(${report.checks.filter((c) => c.ok).length}/${report.checks.length}) ` +
      `env=${report.environment} in ${report.totalMs}ms`;

    if (report.ok) this.logger.log(summary);
    else this.logger.error(summary);
  }

  private async notify(report: ConnectivityReport): Promise<void> {
    const changed = this.lastOk !== report.ok;
    this.lastOk = report.ok;

    // A failure always mails. In `changes` mode a success mails only when it is
    // news — the first run, or a recovery.
    if (CONNECTIVITY_EMAIL_MODE === 'changes' && report.ok && !changed) return;

    const failed = report.checks.filter((c) => !c.ok).map((c) => c.name);
    const passed = report.checks.filter((c) => c.ok).length;

    const subject = report.ok
      ? `[ForgeStack ${report.environment}] Connectivity OK — ${passed}/${report.checks.length} passed`
      : `[ForgeStack ${report.environment}] Connectivity FAILED — ${failed.join(', ')}`;

    await this.email.send({
      to: CONNECTIVITY_ALERT_EMAIL,
      subject,
      html: this.renderHtml(report),
      typeCode: 'connectivity_check',
    });

    this.logger.log(`connectivity report emailed to ${CONNECTIVITY_ALERT_EMAIL}`);
  }

  private renderHtml(report: ConnectivityReport): string {
    const cell = 'padding:8px 12px;border-bottom:1px solid #e5e7eb';

    const rows = report.checks
      .map(
        (check) => `
        <tr>
          <td style="${cell};font-family:monospace">${escapeHtml(check.name)}</td>
          <td style="${cell};color:${check.ok ? '#047857' : '#b91c1c'};font-weight:600">${
            check.ok ? 'OK' : 'FAILED'
          }</td>
          <td style="${cell};text-align:right">${check.durationMs} ms</td>
          <td style="${cell};color:#374151">${escapeHtml(check.detail)}</td>
        </tr>`
      )
      .join('');

    return `
      <div style="font-family:system-ui,-apple-system,Segoe UI,sans-serif;max-width:760px">
        <h2 style="margin:0 0 4px;color:${report.ok ? '#047857' : '#b91c1c'}">
          Connectivity ${report.ok ? 'OK' : 'FAILED'}
        </h2>
        <p style="margin:0 0 16px;color:#6b7280">
          Environment <strong>${escapeHtml(report.environment)}</strong> ·
          ${report.ranAt.toISOString()} · completed in ${report.totalMs} ms
        </p>
        <table style="border-collapse:collapse;width:100%;font-size:14px">
          <thead>
            <tr style="text-align:left;background:#f9fafb">
              <th style="padding:8px 12px">Check</th>
              <th style="padding:8px 12px">Result</th>
              <th style="padding:8px 12px;text-align:right">Took</th>
              <th style="padding:8px 12px">Detail</th>
            </tr>
          </thead>
          <tbody>${rows}</tbody>
        </table>
        <p style="margin:16px 0 0;color:#6b7280;font-size:12px">
          Schedule <code>${escapeHtml(CONNECTIVITY_CHECK_CRON)}</code>
          (${escapeHtml(CONNECTIVITY_CHECK_TIMEZONE)}). Set CONNECTIVITY_CHECK_CRON
          to change it, or HEALTHCHECK_EMAIL_MODE=changes to receive only failures
          and recoveries.
        </p>
      </div>`;
  }
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
